import { isNil } from "lodash";
import { buffers, END, EventChannel, eventChannel } from "redux-saga";

import { getFullyQualifiedUrl } from "lib/fetch";

export type UploadFileChannelEvent =
  | { progress: number }
  | { success: true; response: unknown }
  | { err: string; status_code?: number; response?: unknown };

export function createUploadFileChannel(
  url: string,
  file: File,
  formData: Record<string, unknown> | undefined,
  auth: AuthSlice,
  method: HttpUpdateMethod = "POST",
  fieldName: string = "file",
): EventChannel<UploadFileChannelEvent> {
  return eventChannel<UploadFileChannelEvent>(emitter => {
    // Preparing the xhr and the data
    const xhr = new XMLHttpRequest();
    const data = new FormData();
    data.append(fieldName, file);

    if (formData) {
      Object.keys(formData).forEach(key => {
        if (!isNil(formData[key])) {
          data.append(key, formData[key] as string | Blob);
        }
      });
    }

    // Adding the listeners
    const onProgress = (e: ProgressEvent) => {
      if (e.lengthComputable) {
        const progress = e.loaded / e.total;
        emitter({ progress });
      }
    };
    const onFailure = (statusCode?: number, response?: unknown) => {
      emitter({ err: "Upload failed", status_code: statusCode, response });
      emitter(END);
    };
    // An upload error or abort reports no status of its own.
    const onUploadFailure = () => onFailure();

    // A rejection body carries the reason (eg. DRF field errors); callers that
    // don't want it just ignore the extra key.
    const parseResponse = () => {
      try {
        return JSON.parse(xhr.response);
      } catch {
        return null;
      }
    };

    xhr.upload.addEventListener("progress", onProgress);
    xhr.upload.addEventListener("error", onUploadFailure);
    xhr.upload.addEventListener("abort", onUploadFailure);
    xhr.onreadystatechange = () => {
      const { readyState, status, response } = xhr;
      if (readyState === 4) {
        if ([200, 201].indexOf(status) !== -1) {
          const jsonResponse = JSON.parse(response);
          emitter({ success: true, response: jsonResponse });
          emitter(END);
        } else {
          onFailure(status, parseResponse());
        }
      }
    };

    // Send file uploads to the api specific endpoint
    xhr.open(method, getFullyQualifiedUrl(url), true);
    xhr.withCredentials = true;
    const userRoleSlug = auth.activeRole ? auth.activeRole.slug : "";
    if (userRoleSlug) {
      xhr.setRequestHeader("X-User-Role", userRoleSlug);
    }
    xhr.send(data);
    return () => {
      xhr.upload.removeEventListener("progress", onProgress);
      xhr.upload.removeEventListener("error", onUploadFailure);
      xhr.upload.removeEventListener("abort", onUploadFailure);
      xhr.onreadystatechange = null;
      xhr.abort();
    };
  }, buffers.sliding(2));
}
